import React, { createContext, useContext, useState, useCallback, useRef, useEffect } from 'react';
import { TarotCard } from '@/context/CardsContext';
import { DailyReadingService } from '@/services/dailyReadingService';
import { useAuth } from '@/context/AuthContext';
import { useTranslation } from 'react-i18next';
import cardsData from '@/constants/cards.json';

interface DailyReadingContextType {
    // State
    selectedCard: TarotCard | null;
    isCardFlipped: boolean;
    readingId: string | null;
    isCreatingReading: boolean;
    readingComplete: boolean;

    // Actions
    selectCard: (card: TarotCard) => Promise<void>;
    resetReading: () => void;
}

const DailyReadingContext = createContext<DailyReadingContextType | undefined>(undefined);

interface DailyReadingProviderProps {
    children: React.ReactNode;
}

export const DailyReadingProvider: React.FC<DailyReadingProviderProps> = ({ children }) => {
    const { refreshUserData, user } = useAuth();
    const { i18n } = useTranslation();

    // State
    const [selectedCard, setSelectedCard] = useState<TarotCard | null>(null);
    const [isCardFlipped, setIsCardFlipped] = useState(false);
    const [readingId, setReadingId] = useState<string | null>(null);
    const [isCreatingReading, setIsCreatingReading] = useState(false);
    const [readingComplete, setReadingComplete] = useState(false);

    // Refs for polling
    const pollingTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const pollingAttemptsRef = useRef(0);

    /**
     * Poll the reading status to check if it's complete
     */
    const checkReadingCompletion = useCallback(async (readingIdToCheck: string) => {
        const maxAttempts = 50; // Poll for up to 30 attempts
        const pollInterval = 2000; // Poll every 2 seconds
        pollingAttemptsRef.current = 0;

        const pollReading = async () => {
            try {
                // Refresh user data to get updated daily reading
                const updatedUser = await refreshUserData();

                // Check if the reading has been completed
                const dailyReading = updatedUser?.dailyReading as any;
                console.log('dailyReading', dailyReading?.interpretation);
                if (dailyReading?._id === readingIdToCheck &&
                    dailyReading?.interpretation?.detailed_reading?.cards?.length === 1) {
                    console.log('Reading complete!');
                    setReadingComplete(true);
                    return;
                }

                pollingAttemptsRef.current++;
                if (pollingAttemptsRef.current < maxAttempts) {
                    pollingTimeoutRef.current = setTimeout(pollReading, pollInterval);
                } else {
                    console.log('Max polling attempts reached');
                    // Still enable the button after max attempts
                    setReadingComplete(true);
                }
            } catch (error) {
                console.error('Error polling reading status:', error);
                pollingAttemptsRef.current++;
                if (pollingAttemptsRef.current < maxAttempts) {
                    pollingTimeoutRef.current = setTimeout(pollReading, pollInterval);
                }
            }
        };

        pollReading();
    }, [refreshUserData]);

    // Cleanup polling on unmount
    useEffect(() => {
        return () => {
            if (pollingTimeoutRef.current) {
                clearTimeout(pollingTimeoutRef.current);
            }
        };
    }, []);

    // Initialize from user.dailyReading if it exists
    useEffect(() => {
        if (!user?.dailyReading) {
            return;
        }

        const dailyReading = user.dailyReading as any;

        // Only initialize if we don't already have a selected card
        if (selectedCard) {
            return;
        }

        // Check if there's a card in the daily reading
        if (dailyReading.cards && dailyReading.cards.length > 0) {
            const cardData = dailyReading.cards[0];

            // Find the card from cards.json
            const foundCard = cardsData.cards.find((c: any) => c.id === cardData.id);

            if (foundCard) {
                const card: TarotCard = {
                    id: foundCard.id,
                    card_name: foundCard.card_name,
                    description: {
                        primary_meaning: foundCard.primary_meaning,
                        interpretation: foundCard.interpretation,
                        reversed: foundCard.reversed
                    },
                    visible: true,
                    reversed: cardData.reversed || false
                };

                // Set the card as selected and flipped
                setSelectedCard(card);
                setIsCardFlipped(true);
                setReadingId(dailyReading._id);

                // Check if reading is complete
                const hasDetailedReading = dailyReading.interpretation?.detailed_reading?.final_conclusion;
                if (hasDetailedReading) {
                    setReadingComplete(true);
                } else {
                    // Start polling if not complete
                    checkReadingCompletion(dailyReading._id);
                }
            }
        }
    }, [user?.dailyReading, selectedCard, checkReadingCompletion]);

    /**
     * Select a card and create a daily reading
     */
    const selectCard = useCallback(async (card: TarotCard) => {
        setSelectedCard(card);

        // Flip the card after a short delay for better UX
        setTimeout(() => {
            setIsCardFlipped(true);
        }, 300);

        // Create the payload and send to backend
        setIsCreatingReading(true);
        try {
            const token = localStorage.getItem('token');
            if (!token) {
                console.error('No auth token found');
                setIsCreatingReading(false);
                return;
            }

            const currentLanguage = i18n.language || 'pt-BR';

            // Get existing dailyReading._id if it exists
            const existingDailyReadingId = (user?.dailyReading as any)?._id;

            const payload = DailyReadingService.createPayload(card, currentLanguage, existingDailyReadingId);
            console.log('Sending daily reading payload:', payload);
            console.log('Creating daily reading:', payload);
            const reading = await DailyReadingService.createReading(token, payload);
            console.log('Daily reading created:', reading);

            setReadingId(reading._id);

            // Start polling to check if reading is complete
            checkReadingCompletion(reading._id);
        } catch (error) {
            console.error('Error creating daily reading:', error);
        } finally {
            setIsCreatingReading(false);
        }
    }, [i18n.language, checkReadingCompletion, user?.dailyReading]);

    /**
     * Reset the reading state
     */
    const resetReading = useCallback(() => {
        // Clear any ongoing polling
        if (pollingTimeoutRef.current) {
            clearTimeout(pollingTimeoutRef.current);
            pollingTimeoutRef.current = null;
        }

        setSelectedCard(null);
        setIsCardFlipped(false);
        setReadingId(null);
        setIsCreatingReading(false);
        setReadingComplete(false);
        pollingAttemptsRef.current = 0;
    }, []);

    const value: DailyReadingContextType = {
        selectedCard,
        isCardFlipped,
        readingId,
        isCreatingReading,
        readingComplete,
        selectCard,
        resetReading,
    };

    return (
        <DailyReadingContext.Provider value={value}>
            {children}
        </DailyReadingContext.Provider>
    );
};

/**
 * Hook to use the DailyReading context
 */
export const useDailyReading = (): DailyReadingContextType => {
    const context = useContext(DailyReadingContext);
    if (!context) {
        throw new Error('useDailyReading must be used within a DailyReadingProvider');
    }
    return context;
};

